import { CircularProgress } from "@mui/material";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  Error,
  getCompanyName,
  HStack,
  VStack,
} from "../../components/utils";

import { generateSH6Report } from "../../api/Esop";
import { Input, Label } from "../../components/shared/InputField";
import { MultiSelect, OptionTypes } from "../../components/shared/MultiSelect";
import { Select } from "../../components/shared/Select";
import { useEsopPlans, useGetScheduledReports } from "../../queries";
import { PlanState, SH6ReportReq } from "../../types/EsopPlan";
import { ReportDto } from "../../types/Grant";
import { formatDate } from "../../utils/date";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../utils/DownloadFile";
import { HissaReportType, ScheduleParams } from "../../types/common";
import ScheduleSection from "../reports/dialogs/ScheduleSection";
import { useGetAssociatedUsers } from "../../queries/usersRoles";
import ColumnChooserSection from "../reports/dialogs/ColumnChooserSection";
import { getReportColumns } from "../reports/utils/reportColumns";
import {
  deleteReportPreset,
  getReportPresets,
  saveReportPreset,
} from "../../api/reportPresets";
import { AvailableTableFilters, TableName } from "../../types/Table";

function SH6ReportDialog({
  onClose = () => {},
}: Readonly<{ onClose: () => void }>) {
  const { data: _plans } = useEsopPlans();
  const plans = _plans || [];
  const planOptions: OptionTypes[] = [];
  const [shouldPiiBeMasked, setShouldPiiBeMasked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scheduleParams, setScheduleParams] = useState<
    ScheduleParams | undefined
  >(undefined);
  const reportColumns = getReportColumns(HissaReportType.SH6_REPORT);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    reportColumns.map((c) => c.key)
  );
  const [customHeaders, setCustomHeaders] = useState<Record<string, string>>(
    {}
  );
  const [showColumnChooser, setShowColumnChooser] = useState(false);
  const { data: scheduledReports, refetch: refetchScheduledReports } =
    useGetScheduledReports();
  const existingSchedule = scheduledReports?.find(
    (s) => s.reportType === HissaReportType.SH6_REPORT && s.isActive
  );
  const { data: associatedUsers } = useGetAssociatedUsers();

  const reportPresetTableName: TableName = `REPORT_COLUMNS_${HissaReportType.SH6_REPORT}`;
  const [savePresetOnGenerate, setSavePresetOnGenerate] =
    useState<boolean>(false);
  const [presetName, setPresetName] = useState<string>("");
  const [baselineColumnState, setBaselineColumnState] = useState(() =>
    JSON.stringify({
      selectedColumns: reportColumns.map((c) => c.key),
      customHeaders: {},
    })
  );
  const [reportPresets, setReportPresets] = useState<AvailableTableFilters[]>(
    []
  );
  const [isPresetLoading, setIsPresetLoading] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string>("");
  const [chooserOrderSyncToken, setChooserOrderSyncToken] = useState(0);

  useEffect(() => {
    getReportPresets(reportPresetTableName).then((presets) => {
      setReportPresets(presets || []);
    });
  }, []);

  const getFilteredHeaders = (
    selected: string[],
    headers: Record<string, string>
  ) =>
    Object.fromEntries(
      Object.entries(headers).filter(
        ([key, value]) => selected.includes(key) && Boolean(value?.trim())
      )
    );

  const getComparableColumnState = () =>
    JSON.stringify({
      selectedColumns,
      customHeaders: getFilteredHeaders(selectedColumns, customHeaders),
    });

  const hasColumnSelectionChanges =
    Boolean(baselineColumnState) &&
    baselineColumnState !== getComparableColumnState();

  const applyPreset = (preset: AvailableTableFilters) => {
    const state = (preset.filterState || {}) as {
      selectedColumns?: string[];
      customHeaders?: Record<string, string>;
      columnLabelSnapshot?: Record<string, string>;
    };
    const presetSelectedColumns = Array.isArray(state.selectedColumns)
      ? state.selectedColumns
      : [];
    const availableColumnKeys = new Set(reportColumns.map((c) => c.key));
    const validSelectedColumns = presetSelectedColumns.filter((key) =>
      availableColumnKeys.has(key)
    );
    if (!validSelectedColumns.length) {
      toast("This preset has no matching columns for the current report", {
        type: "error",
        autoClose: 2500,
      });
      return;
    }
    const selectedSet = new Set(validSelectedColumns);
    const nextCustomHeaders = Object.fromEntries(
      Object.entries(state.customHeaders || {}).filter(
        ([key, value]) =>
          selectedSet.has(key) && Boolean(`${value || ""}`.trim())
      )
    );
    setSelectedColumns(validSelectedColumns);
    setCustomHeaders(nextCustomHeaders);
    setChooserOrderSyncToken((prev) => prev + 1);
    setSelectedPresetId(preset.id);
    setSavePresetOnGenerate(false);
    setPresetName("");
    setBaselineColumnState(
      JSON.stringify({
        selectedColumns: validSelectedColumns,
        customHeaders: nextCustomHeaders,
      })
    );
    toast("Preset applied", { type: "success", autoClose: 1800 });
  };

  const deleteSelectedPreset = async () => {
    if (!selectedPresetId) {
      toast("Please select a preset to delete", {
        type: "error",
        autoClose: 1800,
      });
      return;
    }
    try {
      setIsPresetLoading(true);
      await deleteReportPreset(selectedPresetId);
      const presets = await getReportPresets(reportPresetTableName);
      setReportPresets(presets || []);
      setSelectedPresetId("");
      toast("Preset deleted", { type: "success", autoClose: 1800 });
    } catch (_error) {
      toast("Unable to delete preset", { type: "error", autoClose: 2200 });
    } finally {
      setIsPresetLoading(false);
    }
  };

  const saveCurrentPreset = async (nameOverride?: string) => {
    const cleanName = (nameOverride ?? presetName).trim();
    if (!cleanName) {
      toast("Please enter preset name", { type: "error", autoClose: 1800 });
      return;
    }
    if (!selectedColumns.length) {
      toast("Please select at least one column", {
        type: "error",
        autoClose: 1800,
      });
      return;
    }
    const duplicate = reportPresets.some(
      (preset) => preset.name?.toLowerCase() === cleanName.toLowerCase()
    );
    if (duplicate) {
      toast("A preset with this name already exists", {
        type: "error",
        autoClose: 2200,
      });
      return;
    }
    try {
      setIsPresetLoading(true);
      await saveReportPreset({
        tableName: reportPresetTableName,
        filterName: cleanName,
        filterState: {
          version: 1,
          selectedColumns,
          customHeaders,
          columnLabelSnapshot: Object.fromEntries(
            reportColumns.map((column) => [column.key, column.label])
          ),
        },
      });
      const presets = await getReportPresets(reportPresetTableName);
      setReportPresets(presets || []);
      setPresetName("");
      setSavePresetOnGenerate(false);
      setBaselineColumnState(getComparableColumnState());
      toast("Preset saved", { type: "success", autoClose: 1800 });
    } catch (_error) {
      toast("Error saving preset", { type: "error", autoClose: 1800 });
    } finally {
      setIsPresetLoading(false);
    }
  };

  const availableGrantStateOptions = ["GRANTED", "OFFERED"];
  const ids: string[] = [];
  plans
    .filter((p) => p.esopPlanState === PlanState.ACTIVE)
    .forEach((plan) => {
      planOptions.push({ value: plan.planName });
      ids.push(plan.esopPlanId);
    });

  const initialValues: SH6ReportReq = {
    date: formatDate(new Date().toUTCString()),
    planIds: ids,
    maskPiiData: false,
    grantStates: availableGrantStateOptions,
  };
  const validationSchema = Yup.object({
    date: Yup.date().required("required"),
    grantStates: Yup.array()
      .test("notEmpty", "Select atleast one state", (value) => {
        if (value && value.length > 0) {
          return true;
        }
        return false;
      })
      .required("Grant states are required"),
    planIds: Yup.array()
      .test("notEmpty", "Select a plan", (value) => {
        if (value && value.length > 0) {
          return true;
        }
        return false;
      })
      .required("plan required"),
  });

  const fileName = `SH-6 Report_${getCompanyName()}`;

  async function downloadReport(sh6Report: ReportDto) {
    onClose();
    const blob = convertBase64ToBlob(sh6Report.base64File, sh6Report.fileType);
    downloadBlobObject(await blob, fileName);
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      values.maskPiiData = shouldPiiBeMasked;
      values.selectedColumns = selectedColumns;
      values.customHeaders = Object.fromEntries(
        Object.entries(customHeaders).filter(
          ([key, value]) =>
            selectedColumns.includes(key) && Boolean(value?.trim())
        )
      );
      if (values.planIds.length > 0) {
        setIsLoading(true);
        if (savePresetOnGenerate && hasColumnSelectionChanges) {
          if (!presetName.trim()) {
            toast("Preset name is mandatory when save preset is checked", {
              type: "error",
              autoClose: 1800,
            });
            setIsLoading(false);
            return;
          }
          try {
            await saveCurrentPreset(presetName.trim());
          } catch (_error) {
            setIsLoading(false);
            return;
          }
        }
        const payload =
          scheduleParams && scheduleParams.recipients.length > 0
            ? { ...values, scheduleParams }
            : values;
        generateSH6Report(payload as SH6ReportReq)
          .then((res) => {
            setIsLoading(false);
            refetchScheduledReports();
            toast(
              scheduleParams && scheduleParams.recipients.length > 0
                ? "Report scheduled successfully"
                : "Please wait while your report is being generated",
              { type: "info", autoClose: 2000 }
            );
            onClose();
          })
          .catch(() => {
            toast("Something Went Wrong!", { type: "error", autoClose: 2000 });
            setIsLoading(false);
          });
      }
    },
  });
  return (
    <VStack className=" min-h-min">
      <div className="p-4 text-lg font-medium border-b ">
        <h6 className="flex justify-between">
          Generating SH-6
          <span className="cursor-pointer" onClick={() => onClose()}>
            X
          </span>
        </h6>
      </div>
      <VStack className="gap-2">
        {existingSchedule && (
          <div className="flex items-start gap-2 mx-4 mt-4 bg-orange-50 border border-orange-200 rounded-md px-4 py-3 text-sm text-orange-800">
            <Icon
              icon="mdi:clock-alert-outline"
              width={16}
              className="mt-0.5 shrink-0 text-orange-500"
            />
            <span>
              This report is already scheduled (runs every{" "}
              <strong>{existingSchedule.intervalDays}</strong> day
              {existingSchedule.intervalDays !== 1 ? "s" : ""}). Submitting
              again will create an additional schedule.
            </span>
          </div>
        )}
        <VStack className="w-full max-w-xl p-4">
          <Label> Plans</Label>
          <MultiSelect
            _className=""
            multiple={true}
            placeholder={"--Select plans--"}
            options={planOptions}
            handleSelection={(e) => {
              const planIds = plans
                .filter(
                  (p) =>
                    e.includes(p.planName) &&
                    p.esopPlanState === PlanState.ACTIVE
                )
                .map((p) => p.esopPlanId);
              formik.setFieldValue("planIds", planIds);
            }}
            optionValues={plans
              .filter((p) => formik.values.planIds.includes(p.esopPlanId))
              .map((p) => p.planName)}
          />
          {formik.errors.planIds && (
            <Error text={formik.errors.planIds.toString()} />
          )}
        </VStack>
        <HStack className="items-center justify-start gap-12 px-6">
          <VStack>
            <Label className="text-sm font-normal">To Date:</Label>
            <Box>
              <Input
                type="date"
                value={formik.values.date}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  if (!e.target.value) return;
                  formik.setFieldValue("date", e.target.value);
                }}
              />
            </Box>
          </VStack>
          <VStack className="min-w-80 mx-2">
            <VStack className="w-full max-w-xl p-4">
              <Label> Grant States</Label>
              <MultiSelect
                _className=""
                multiple={true}
                placeholder={"--Select states--"}
                options={availableGrantStateOptions.map((option) => ({
                  value: option,
                }))}
                handleSelection={(e) => {
                  const grantStates = availableGrantStateOptions
                    .filter((p) => e.includes(p))
                    .map((p) => p);
                  formik.setFieldValue("grantStates", grantStates);
                }}
                optionValues={availableGrantStateOptions
                  .filter((p) => formik.values.grantStates.includes(p))
                  .map((p) => p)}
              />
              {formik.errors.grantStates && (
                <Error text={formik.errors.grantStates.toString()} />
              )}
            </VStack>
          </VStack>
        </HStack>
        {reportPresets.length > 0 && (
          <HStack className="w-full gap-2 px-4">
            <div className="flex-1">
              <Label className="text-sm font-medium">
                Column Chooser Presets
              </Label>
              <HStack className="gap-2 items-center">
                <Select
                  className="flex-1 min-w-0"
                  options={reportPresets}
                  value={selectedPresetId}
                  placeholder={
                    isPresetLoading
                      ? "Loading presets..."
                      : "--Select a saved preset--"
                  }
                  valueGetter={(preset) => preset.id}
                  textGetter={(preset) => preset.name || "Untitled Preset"}
                  onChange={(e) => {
                    const preset = reportPresets.find(
                      (entry) => entry.id === e.target.value
                    );
                    if (preset) {
                      applyPreset(preset);
                    } else {
                      setSelectedPresetId("");
                    }
                  }}
                />
                {selectedPresetId && (
                  <ButtonPrimary1
                    onClick={deleteSelectedPreset}
                    className="text-red-500 border-red-300 hover:bg-red-50 bg-transparent"
                  >
                    Delete
                  </ButtonPrimary1>
                )}
              </HStack>
            </div>
            <div className="flex-1"></div>
          </HStack>
        )}
        <HStack className="pt-2 items-center gap-2 px-4 before:border-b-2 before:border-dashed before:m-auto after:flex-1 after:border-dashed after:content-[''] after:border-b-2">
          <ButtonPrimary1
            onClick={() => setShowColumnChooser(!showColumnChooser)}
            className="flex flex-row bg-transparent border border-solid cursor-pointer border-orange-501 hover:bg-transparent"
          >
            Column Chooser
            <Icon
              icon="material-symbols:keyboard-arrow-down-rounded "
              className={`${showColumnChooser ? "rotate-180" : ""}`}
            />
          </ButtonPrimary1>
        </HStack>
        {showColumnChooser && (
          <div className="px-4">
            <ColumnChooserSection
              columns={reportColumns}
              selectedColumns={selectedColumns}
              customHeaders={customHeaders}
              onSelectedColumnsChange={setSelectedColumns}
              onCustomHeadersChange={setCustomHeaders}
              sortable
              orderSyncToken={chooserOrderSyncToken}
              footerContent={
                hasColumnSelectionChanges ? (
                  <VStack className="gap-2 mt-1">
                    <HStack className="items-center gap-2">
                      <input
                        type="checkbox"
                        checked={savePresetOnGenerate}
                        onChange={(e) => {
                          setSavePresetOnGenerate(e.target.checked);
                          if (!e.target.checked) setPresetName("");
                        }}
                        className="accent-orange-501"
                      />
                      <span className="text-xs text-gray-700">
                        Save this preset
                      </span>
                    </HStack>
                    {savePresetOnGenerate && (
                      <div className="w-full max-w-sm">
                        <Input
                          type="text"
                          value={presetName}
                          onChange={(e) => setPresetName(e.target.value)}
                          placeholder="Preset name"
                          className="w-full"
                        />
                        <div className="text-xxs text-gray-500 mt-1">
                          Preset name is mandatory when save is checked.
                        </div>
                      </div>
                    )}
                  </VStack>
                ) : null
              }
            />
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-gray-100 px-4">
          <ScheduleSection
            value={scheduleParams}
            onChange={setScheduleParams}
            users={associatedUsers ?? []}
          />
        </div>
        <HStack className="justify-end gap-2 px-4 mt-7">
          <input
            type="checkbox"
            checked={shouldPiiBeMasked}
            onChange={() => setShouldPiiBeMasked(!shouldPiiBeMasked)}
            className="text-white accent-orange-501"
            name=""
            id=""
          />
          <span>Hide all Personally Identifiable Information</span>
        </HStack>
        <HStack className="justify-end m-3">
          <p className="italic">Note: The date filters are inclusive </p>
        </HStack>
        <HStack className="justify-between px-4 py-4">
          <ButtonSecondary
            onClick={() => onClose()}
            className="text-gray-400 bg-slate-50"
          >
            Cancel
          </ButtonSecondary>
          <div className="flex items-center">
            <ButtonPrimary
              disabled={isLoading}
              onClick={() => {
                if (selectedColumns.length === 0) {
                  toast("Please select at least one column", {
                    type: "error",
                    autoClose: 2000,
                  });
                  return;
                }
                if (scheduleParams && scheduleParams.recipients.length === 0) {
                  toast(
                    "Please select at least one recipient to schedule the report",
                    { type: "error", autoClose: 2000 }
                  );
                  return;
                }
                formik.handleSubmit();
              }}
            >
              {scheduleParams && scheduleParams.recipients.length > 0
                ? "Generate & Schedule"
                : "Generate"}
            </ButtonPrimary>
            <div>{isLoading && <CircularProgress size={32} />}</div>
          </div>
        </HStack>
      </VStack>
    </VStack>
  );
}
export default SH6ReportDialog;
